import { Layout } from '../components/Layout';
import { 
  Leaf,
  Heart,
  Camera,
  Sparkles,
  Shield,
  Handshake,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function AboutUsPage() {
  const navigate = useNavigate();

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 relative">
        {/* Close Button */}
        <button
          onClick={() => navigate('/')}
          className="absolute top-4 right-4 md:top-8 md:right-6 w-10 h-10 rounded-full bg-[var(--theme-card)] border border-[var(--theme-border)] flex items-center justify-center hover:bg-[var(--theme-hover)] transition-colors shadow-sm z-10"
          title="Back to Home"
          data-testid="close-about-btn"
        >
          <X className="w-5 h-5 text-[var(--theme-text-secondary)]" />
        </button>

        {/* Hero Section */}
        <section className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-black text-[var(--theme-text-primary)] mb-4">
            About WANTD
          </h1>
          <p className="text-lg text-[var(--theme-text-secondary)] max-w-2xl mx-auto">
            A local marketplace built for community, sustainability, and simplicity.
          </p>
        </section>

        {/* Community Message */}
        <section className="mb-12 bg-gradient-to-br from-[#1E3A5F]/5 to-[#C9956C]/5 rounded-2xl border border-[var(--theme-border)] p-8 md:p-10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#C9956C]/20 flex items-center justify-center flex-shrink-0">
              <Heart className="w-6 h-6 text-[#C9956C]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-3">Support Your Community</h2>
              <p className="text-[var(--theme-text-secondary)] leading-relaxed">
                Have an old hammer lying around? Someone might need that! Support your local community 
                and prevent global wastage by passing on your used goods to those who may need or want them. 
                If you're feeling charitable, you can mark an item as free, and it will match with anyone 
                looking for it - however you might find yourself making some spare change for that old hammer.
              </p>
            </div>
          </div>
        </section>

        {/* About Content */}
        <section className="mb-12">
          <div className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] overflow-hidden">
            {/* Our Vision */}
            <div className="p-8 md:p-10 border-b border-[var(--theme-border)]">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#059669]/10 flex items-center justify-center flex-shrink-0">
                  <Leaf className="w-6 h-6 text-[#059669]" />
                </div>
                <h3 className="text-xl font-bold text-[var(--theme-text-primary)]">Our Vision</h3>
              </div>
              <div className="space-y-4 text-[var(--theme-text-secondary)] leading-relaxed">
                <p>
                  We share a concern that modern consumerism is becoming unsustainable. Instead of asking 
                  a neighbour for a jerry can, we tend to go and buy a new one. Globally, this mentality 
                  creates an unhealthy demand for the perpetual production of things that our communities 
                  don't actually need.
                </p>
                <p>
                  Gone are the days of knocking on your neighbour's door to see if they have a spare ladder 
                  they don't use - WANTD allows you to create that demand in your vicinity, and automatically 
                  source and find a "neighbour" that may have one lying around. Our vision is to reduce global 
                  production rates of new commodity products by promoting the transaction of perfectly good, 
                  used goods within a practical locale.
                </p>
                <p>
                  No need to pay for postage, no need to wait a week for the shared shipping container to arrive. 
                  It's likely that someone near you has something that you want, that they just haven't thought of selling.
                </p>
              </div>
            </div>

            {/* AI Inventory */}
            <div className="p-8 md:p-10 border-b border-[var(--theme-border)] bg-[var(--theme-hover)]">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[var(--theme-card)] flex items-center justify-center flex-shrink-0">
                  <Camera className="w-6 h-6 text-[var(--theme-text-primary)]" />
                </div>
                <h3 className="text-xl font-bold text-[var(--theme-text-primary)]">AI-Powered Inventory</h3>
              </div>
              <div className="space-y-4 text-[var(--theme-text-secondary)] leading-relaxed">
                <p>
                  Our AI powered inventory system makes it completely frictionless to add items to your personal 
                  inventory, ready to be matched with someone wanting it. Simply take one or more photos of items 
                  in your rooms, cupboards, living rooms, garages and sheds, and our image analysis tool detects 
                  the items in each photo, adds a description and suggests an inventory item for you to add.
                </p>
                <p>
                  Don't want to sell your dining table? No problem, just reject the suggestion. The analysis tool 
                  can detect and match known products to brands, models, and product names, or if the item is unique 
                  or unbranded, it produces a descriptive summary that makes it suitable for matching to WANTD ads.
                </p>
              </div>
            </div>

            {/* AI Matching */}
            <div className="p-8 md:p-10 border-b border-[var(--theme-border)]">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#C9956C]/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-[#C9956C]" />
                </div>
                <h3 className="text-xl font-bold text-[var(--theme-text-primary)]">Intelligent Matching</h3>
              </div>
              <p className="text-[var(--theme-text-secondary)] leading-relaxed">
                Our AI matching system automatically connects buyers with your listed items. 
                You may receive a suggested match within seconds of uploading a photo and approving your 
                listings.
              </p>
            </div>

            {/* Trust & Integrity */}
            <div className="p-8 md:p-10 border-b border-[var(--theme-border)] bg-[var(--theme-hover)]">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#059669]/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-[#059669]" />
                </div>
                <h3 className="text-xl font-bold text-[var(--theme-text-primary)]">Trust & Integrity</h3>
              </div>
              <div className="space-y-4 text-[var(--theme-text-secondary)] leading-relaxed">
                <p>
                  Every user has a trust score, built through verified activity, accurate listings, and positive
                  interactions with other users. We want to make sure that every WANTD listing is honest, valid
                  and true, and our trust system helps maintain platform integrity.
                </p>
              </div>
            </div>

            {/* Mission Statement */}
            <div className="p-8 md:p-10 bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F] text-white">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Handshake className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">Our Mission</h3>
              </div>
              <p className="text-white/90 leading-relaxed text-lg">
                At WANTD, we want you, your neighbours and your broader community to engage and transact with 
                each other before buying new consumer items. We hope it will save you money, reduce unnecessary 
                consumption and bring our communities closer together.
              </p>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
